import { supabase } from './supabaseClient';
import { Report } from '../types';
import { saveProjectToCloud } from './cloudService';
import { isProjectSaveConflict, knownProjectVersion } from './projectVersionAnchor';
import { getServiceLog } from './serviceJournal';
import { evaluateSendGuard, fetchPriorSendEvidence } from './sendGuard';
import { evaluateReportRelease, fetchReleaseEvidence } from './reportReleaseGuard';
import { consumeParticipantPrompt, participantPromptKey } from './participantPrompt';
import { DraftDiscardEvidence, DraftDiscardPlanOk } from './draftDiscardLogic';

/**
 * Datenbankseite von „Entwurf verwerfen" — die Regeln stehen in
 * draftDiscardLogic.ts, hier wird nur gelesen und geschrieben.
 *
 * ─── Lesen: fail-closed ─────────────────────────────────────────────────────
 * Scheitert EINE der Abfragen, kommt `lookupFailed` zurueck und der Plan
 * blockiert. Versand-Riegel und Freigabe-Riegel sind fuer den Versand
 * fail-open gebaut (ein Netzfehler darf einen Erstversand nicht bremsen);
 * deshalb werden hier ihre ROHDATEN geholt (`fetchPriorSendEvidence`,
 * `fetchReleaseEvidence`) und deren `lookupFailed` durchgereicht — die
 * Bewertung bleibt die ihre. Fehlende Tabelle, RLS, Netz: alles blockiert.
 * Lieber einmal zu oft „nicht jetzt" als einmal ein Entwurf weg, der schon
 * versendet war oder die Feldarbeit eines Freelancers trug.
 *
 * ─── Schreiben: erst bestaetigt, dann geraeumt, sonst zurueck ──────────────
 *  0. Der Versionsanker (knownProjectVersion, gesetzt beim Laden und bei
 *     jedem Save) MUSS bekannt sein — sonst laesst sich nicht sagen, ob der
 *     Stand, gegen den geplant wurde, noch der auf dem Server ist. Ohne
 *     Anker: Abbruch, „Projekt neu laden". Dann die Vorab-Probe der
 *     Raeum-RPC (Nr. 114) mit leerer Tabellenliste: fehlt sie oder stimmt
 *     der Anker nicht mehr, Abbruch VOR dem ersten Schreiben. Ohne die RPC
 *     gibt es kein Verwerfen — ein ungeprueftes Loeschen als Rueckfall
 *     traefe Zeilen, die ein anderer Client gerade geschrieben hat.
 *  1. Spalten-Vorgriff, BEDINGT — auf die Nummern UND auf genau diesen
 *     Anker (`updated_at = anker`), ohne updated_at zu veraendern. Ein
 *     fremder Zwischenstand traegt einen anderen Anker, das Update trifft
 *     nichts, und hier ist Schluss, bevor irgendetwas geschrieben ist.
 *  2. Der wiederhergestellte Bericht wird DIREKT und bestaetigt gespeichert
 *     (saveProjectToCloud, mit demselben Anker und `onConflict: 'throw'`).
 *     Nicht nur in die Warteschlange: die hat einen 2-s-Aufschub und mehrere
 *     Torwaechter (Projektsperre, BUILD_VERALTET, Altersriegel, Auth);
 *     fielen die Zeilen vorher und der Blob bliebe der Entwurf, ergaebe das
 *     beim naechsten Laden Bericht #N mit den Eintraegen von #N-1. Und nicht
 *     mit der Bergung ('salvage', Vorgabe): die uebernaehme bei einem
 *     fremden Zwischenstand dessen Protokollzeilen und schriebe trotzdem
 *     Berichtsinhalt und Metadaten darueber — fuer einen loeschenden Pfad
 *     ist ein Konflikt kein Fall zum Bergen, sondern zum Abbrechen.
 *     Der Save prunt dabei die Kindtabellen ueber die Behalte-Liste, in
 *     EINER Transaktion (save_project_children_tx) — aber NICHT in derselben
 *     Transaktion wie der Blob. Scheitert er hinter dem Blob, ist der Blob
 *     #N-1 und die Tabellen sind der Entwurf: deshalb loest JEDER Fehler des
 *     Saves, der kein Anker-Konflikt ist, die Kompensation aus (3.).
 *  3. Nur wenn eine Behalte-Liste LEER ist, wird die Tabelle danach
 *     ausdruecklich geraeumt: der Prune laesst eine leere Liste absichtlich
 *     stehen (Datenverlust-Guard vom 21.07.2026). Scheitert das — oder der
 *     Save aus 2. — wird der ENTWURF zurueckgeschrieben (Kompensation):
 *     lieber der alte Stand ganz als der neue halb. Erst wenn auch das
 *     scheitert, ist der Stand uneinheitlich, und die Meldung sagt es so.
 *
 * `updated_at` bleibt in Schritt 1 unangetastet, es ist der Sync-Anker
 * (projectVersionAnchor); wer ihn verstellt, laesst den folgenden Save mit
 * `counters: 'keep-server'` laufen (Vorfall 26073001). Schritt 2 setzt den
 * Anker ueber saveProjectToCloud selbst nach.
 */

interface HistoryRow { inspection_number: number; section_id: string | null; full_report_json: unknown }

/**
 * Ein Fehler mit einem Satz fuer den Anwender. `stand` sagt, was die
 * Datenbank jetzt traegt: 'entwurf' (nichts oder alles zurueckgenommen),
 * 'uneinheitlich' (Bericht gespeichert, Zeilen nicht geraeumt, Kompensation
 * gescheitert — Betreuer informieren).
 */
export class DraftDiscardError extends Error {
    readonly name = 'DraftDiscardError';
    constructor(message: string, readonly stand: 'entwurf' | 'uneinheitlich', readonly ursache?: unknown) {
        super(message);
    }
}

const isRecord = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);

/**
 * Ein Snapshot ist nur brauchbar, wenn er wie ein Bericht aussieht — mit
 * Metadaten UND Eintraegen. Ohne `items` liesse sich der alte Stand nicht
 * herstellen; dann gilt „kein Snapshot" und der Plan blockiert.
 */
export const asSnapshot = (json: unknown): Report | null => {
    if (!isRecord(json) || !isRecord(json.metadata) || !Array.isArray(json.items)) return null;
    return json as unknown as Report;
};

/** Haengt eine Abfrage, soll der Knopf nicht ewig drehen: nach der Frist gilt „gescheitert". */
const PRUEF_FRIST_MS = 20_000;

const mitFrist = <T>(arbeit: Promise<T>): Promise<T> =>
    new Promise<T>((resolve, reject) => {
        const timer = setTimeout(() => reject(new Error('Pruefung ueberschritt die Frist')), PRUEF_FRIST_MS);
        arbeit.then(v => { clearTimeout(timer); resolve(v); }, e => { clearTimeout(timer); reject(e); });
    });

type Kindtabelle = 'inspection_items' | 'project_checklists';

/** Die RPC aus Nr. 114 (supabase/discard_draft_children_tx.sql). */
export const DISCARD_CHILDREN_RPC = 'discard_draft_children_tx';

const istRpcUnbekannt = (error: { code?: string; message?: string } | null | undefined): boolean =>
    error?.code === 'PGRST202' || /could not find the function/i.test(error?.message || '');

/**
 * Alle Zeilen der genannten Kindtabellen des Projekts raeumen — nur fuer den
 * Fall der leeren Behalte-Liste.
 *
 * VERSIONSGEPRUEFT ueber die RPC aus Nr. 114: sie sperrt die Projektzeile,
 * vergleicht `projects.updated_at` mit `anker` (dem Stand nach dem eigenen
 * Save) und loescht nur bei Gleichheit, in einer Transaktion. Ein nacktes
 * `delete … where project_id` traefe auch Zeilen, die ein anderer Client
 * zwischen Save und Raeumen geschrieben hat (Codex P1, 22.09.2026) — deshalb
 * gibt es hier KEINEN ungeprueften Rueckfall: fehlt die RPC, kommt `'fehlt'`,
 * und der Aufrufer bricht ab (die Vorab-Probe unten stellt das VOR dem ersten
 * Schreiben fest).
 *
 * Mit LEERER Tabellenliste ist der Aufruf die Probe: die RPC sperrt, vergleicht
 * und loescht nichts — das sagt, ob sie da ist und ob der Anker noch stimmt.
 *
 * Liefert `'version'`, wenn die RPC den Stand als ueberholt abweist — dann hat
 * jemand anderes geschrieben, und der Aufrufer darf NICHT kompensieren (das
 * ueberschriebe den fremden Stand), sondern meldet den Stand als uneinheitlich.
 */
export const raeumeTabellen = async (
    projectId: string,
    tables: readonly Kindtabelle[],
    anker: string,
): Promise<'ok' | 'version' | 'fehlt'> => {
    const { data, error } = await supabase.rpc(DISCARD_CHILDREN_RPC, {
        p_project_id: projectId,
        p_expected_updated_at: anker,
        p_tables: [...tables],
    });
    if (error) {
        if (istRpcUnbekannt(error)) return 'fehlt';
        throw error;
    }
    const antwort = isRecord(data) ? data : {};
    return antwort.ok === true ? 'ok' : 'version';
};

const RPC_FEHLT_TEXT = 'Die Datenbank kennt das Verwerfen noch nicht (Migration Nr. 114, discard_draft_children_tx.sql, ist auf dieser Umgebung nicht gefahren). Der Entwurf ist unveraendert.';

export const collectDraftDiscardEvidence = async (projectId: string, live: Report): Promise<DraftDiscardEvidence> => {
    const leer: DraftDiscardEvidence = {
        dbLastExportedNr: null,
        draftArchiveRows: [],
        sendDecision: null,
        previousSnapshot: null,
        parentServiceLog: null,
        freelancerRelease: null,
    };
    const draftNr = live.metadata.inspectionNumber || 0;
    const previousNr = draftNr - 1;
    const linkConfig = live.metadata.globalData?.linkedProjectConfig;
    const parentId = linkConfig?.role === 'child' ? linkConfig.linkedProjectId : null;

    try {
        const [projRes, histRes, sendEvidence, parentRes, releaseEvidence] = await mitFrist(Promise.all([
            supabase.from('projects').select('last_exported_inspection_number').eq('id', projectId).maybeSingle(),
            previousNr >= 1
                ? supabase.from('report_history').select('id, inspection_number, section_id, full_report_json').eq('project_id', projectId).eq('inspection_number', previousNr)
                : Promise.resolve({ data: [] as HistoryRow[], error: null }),
            fetchPriorSendEvidence(projectId),
            parentId
                ? supabase.from('projects').select('data').eq('id', parentId).maybeSingle()
                : Promise.resolve(null),
            fetchReleaseEvidence(projectId),
        ]));

        if (projRes.error) throw projRes.error;
        if (!projRes.data) throw new Error('Projektzeile nicht gefunden');
        if (histRes.error) throw histRes.error;
        if (sendEvidence.lookupFailed) throw new Error('Sendeberichte/Archiv nicht lesbar');
        if (releaseEvidence.lookupFailed) throw new Error('Freelancer-Auftraege nicht lesbar');

        let parentServiceLog: DraftDiscardEvidence['parentServiceLog'] = null;
        if (parentId) {
            if (!parentRes || parentRes.error) throw parentRes?.error || new Error('Mutterprojekt nicht lesbar');
            if (!parentRes.data) throw new Error('Mutterprojekt nicht gefunden');
            // Bewusst der JSON-Stand des Mutterblobs (getServiceLog ohne
            // Journal): dieselbe Quelle, aus der die Tochterbuchung beim
            // Versand geschrieben wird. Ein Mutterblob, der eine Buchung
            // verloren hat, laesst diesen Riegel aus — dafuer stehen die
            // Riegel auf der Mutter selbst (Nr. 44/66).
            parentServiceLog = getServiceLog((parentRes.data as { data?: Report }).data);
        }

        // Mehrere Archivzeilen zur selben Nummer gibt es nur mit Abschnitten;
        // dann ist nicht entscheidbar, welche der Stand vor dem Anlegen war.
        const prevRows = ((histRes.data || []) as HistoryRow[]).filter(r => r.inspection_number === previousNr);
        const meta = live.metadata;
        const sendDecision = evaluateSendGuard(
            { inspectionNumber: draftNr, sectionId: meta.sectionId, reportDate: meta.date },
            sendEvidence,
        );
        const release = evaluateReportRelease({ reportDate: meta.date }, releaseEvidence);

        return {
            dbLastExportedNr: (projRes.data as { last_exported_inspection_number?: number | null }).last_exported_inspection_number ?? null,
            draftArchiveRows: (sendEvidence.history || [])
                .filter(h => h.inspection_number === draftNr)
                .map(h => ({ inspectionNumber: h.inspection_number, sectionId: h.section_id ?? null })),
            sendDecision: { alreadySent: sendDecision.alreadySent, reason: sendDecision.reason },
            previousSnapshot: prevRows.length === 1 ? asSnapshot(prevRows[0].full_report_json) : null,
            previousSnapshotAmbiguous: prevRows.length > 1,
            parentServiceLog,
            freelancerRelease: { blockiert: release.blockiert, hinweis: release.hinweis },
        };
    } catch (e) {
        console.warn('Entwurf verwerfen: Pruefung fehlgeschlagen — es wird nicht verworfen.', e);
        return { ...leer, lookupFailed: true };
    }
};

/** Supabase-Fehler sind keine Error-Instanzen, tragen aber `message`. */
const ursacheText = (e: unknown): string => {
    if (e instanceof Error) return e.message;
    if (isRecord(e) && typeof e.message === 'string') return e.message;
    return String(e);
};

/**
 * Schreibt: Spalten-Vorgriff (bedingt) → Bericht (bestaetigt, Konflikt =
 * Abbruch) → leere Tabellen raeumen (sonst Kompensation).
 *
 * `live` ist der Entwurf, wie er gerade im Editor steht — fuer die
 * Kompensation, falls das Raeumen scheitert. Der Aufrufer legt den
 * wiederhergestellten Bericht danach zusaetzlich in die Warteschlange
 * (onUpdate), damit deren wartender Entwurfs-Eintrag durch ihn ersetzt wird
 * und nichts Altes nachlaeuft.
 */
export const executeDraftDiscard = async (projectId: string, plan: DraftDiscardPlanOk, live: Report): Promise<void> => {
    // 0. Ohne bekannten Anker kein loeschender Schreibvorgang (siehe Kopf).
    const anker = knownProjectVersion(projectId);
    if (!anker) {
        throw new DraftDiscardError('Der Versionsstand des Projekts ist nicht bekannt. Der Entwurf ist unveraendert — bitte das Projekt neu laden und erneut versuchen.', 'entwurf');
    }

    // 0b. Vorab-Probe der Raeum-RPC, VOR dem ersten Schreiben: mit leerer
    // Tabellenliste sperrt sie, vergleicht den Anker und loescht nichts.
    // Fehlt sie (Nr. 114 nicht gefahren), bricht das Verwerfen hier ab —
    // ein ungeprueftes Loeschen als Rueckfall gibt es nicht. Stimmt der
    // Anker nicht mehr, ebenso: dann hat jemand anderes gespeichert.
    const probe = await raeumeTabellen(projectId, [], anker);
    if (probe === 'fehlt') throw new DraftDiscardError(RPC_FEHLT_TEXT, 'entwurf');
    if (probe === 'version') {
        throw new DraftDiscardError('Das Projekt wurde zwischenzeitlich von anderer Stelle gespeichert. Der Entwurf ist unveraendert — bitte das Projekt neu laden und erneut pruefen.', 'entwurf');
    }

    // 1. KEIN updated_at im Payload — siehe Kopf.
    //
    // BEDINGT, zweifach: die Zeile wird nur getroffen, wenn sie noch den
    // Anker traegt, gegen den geplant wurde (ein fremder Save bewegt
    // updated_at), UND wenn die Datenbank den Entwurf noch als laufenden,
    // unversendeten Bericht fuehrt. Trifft das Update nichts, ist hier
    // Schluss — bevor irgendetwas geschrieben ist.
    //
    // N-1 zaehlt ebenfalls als „noch der Entwurf": so steht die Spalte nach
    // einem Verwerfen, das hinter dem Spalten-Update abgebrochen ist. Der
    // Bericht selbst ist dann noch der Entwurf, und der zweite Anlauf muss
    // durchkommen. NULL zaehlt auch: Altprojekte ohne gesetzten Zaehler haben
    // sich nicht „geaendert", sie hatten nie einen.
    const { data: getroffen, error: colError } = await supabase
        .from('projects')
        .update(plan.columns)
        .eq('id', projectId)
        .eq('updated_at', anker)
        .or(`current_inspection_number.is.null,current_inspection_number.in.(${plan.previousNr},${plan.draftNr})`)
        .or(`last_exported_inspection_number.is.null,last_exported_inspection_number.lt.${plan.draftNr}`)
        .select('id')
        .maybeSingle();
    if (colError) throw colError;
    if (!getroffen) {
        throw new DraftDiscardError(`Der Stand von Bericht #${plan.draftNr} hat sich inzwischen geaendert (anderswo gespeichert, versendet oder naechste Begehung angelegt). Der Entwurf ist unveraendert — bitte das Projekt neu laden.`, 'entwurf');
    }

    // Tabellen, deren Behalte-Liste leer ist: der Prune im Save laesst sie
    // absichtlich stehen, sie werden danach ausdruecklich geraeumt. Gilt fuer
    // den wiederhergestellten Bericht (Schritt 3) wie fuer die Kompensation.
    const leereTabellenVon = (listen: ReadonlyArray<readonly ['inspection_items' | 'project_checklists', ReadonlyArray<{ id?: string } | null | undefined> | undefined]>) =>
        listen.filter(([, rows]) => !(rows || []).some(r => !!r?.id)).map(([table]) => table);

    // Kompensation: den Entwurf zurueckschreiben, samt seiner Zeilen (der
    // Save upsertet sie aus live.items / live.checklists) und Spalten (aus
    // seinen Metadaten). Auch hier mit Anker und `throw`, NICHT mit Bergung:
    // die Bergung rettet nur Protokollzeilen und schriebe Berichtsinhalt,
    // Notizen und Metadaten eines fremden Zwischenstands ueber (Codex P1).
    // Der Anker ist der Stand nach dem eigenen Save (ist der Blob schon
    // geschrieben, hat saveProjectToCloud ihn nachgezogen), sonst der alte.
    // Hat der Entwurf selbst eine LEERE Liste (etwa keine Eintraege), und
    // Schritt 2 hat die des Vorberichts zurueckgeschrieben, muss auch die
    // Kompensation raeumen — sonst meldet sie „unveraendert" und der Entwurf
    // traegt beim Neuladen die Eintraege von #N-1.
    const kompensiere = async (grund: string, e: unknown): Promise<never> => {
        try {
            const ankerVorher = knownProjectVersion(projectId) || anker;
            await saveProjectToCloud(live, projectId, undefined, undefined, undefined, { expectedUpdatedAt: ankerVorher, onConflict: 'throw' });
            const ankerDanach = knownProjectVersion(projectId) || ankerVorher;
            const leer = leereTabellenVon([['inspection_items', live.items], ['project_checklists', live.checklists]]);
            if (leer.length > 0) {
                const ergebnis = await raeumeTabellen(projectId, leer, ankerDanach);
                if (ergebnis === 'version') throw new Error('Raeumen abgewiesen: das Projekt wurde nach dem Zurueckschreiben von anderer Stelle gespeichert');
                if (ergebnis === 'fehlt') throw new Error(RPC_FEHLT_TEXT);
            }
        } catch (zurueck) {
            console.error('Entwurf verwerfen: Schreiben UND Kompensation gescheitert', e, zurueck);
            throw new DraftDiscardError(
                `Der Stand ist UNEINHEITLICH: ${grund}, und der Entwurf liess sich nicht zurueckschreiben. Bitte das Projekt neu laden und den Betreuer informieren. (${ursacheText(e)}; ${ursacheText(zurueck)})`,
                'uneinheitlich', e,
            );
        }
        throw new DraftDiscardError(`${grund}; der Entwurf wurde unveraendert zurueckgeschrieben. Bitte erneut versuchen. (${ursacheText(e)})`, 'entwurf', e);
    };

    // 2. Bestaetigt, mit demselben Anker; ein fremder Zwischenstand ist ein
    // Abbruch, keine Bergung — und dann ist nichts geschrieben (der Anker
    // wird vor dem Update geprueft; das Spalten-Update von eben traegt ihn
    // noch). Jeder ANDERE Fehler kann hinter dem Blob liegen (Kindtabellen-
    // RPC) — dann zurueck auf den Entwurf.
    try {
        await saveProjectToCloud(plan.restored, projectId, undefined, undefined, undefined, { expectedUpdatedAt: anker, onConflict: 'throw' });
    } catch (e) {
        if (isProjectSaveConflict(e)) {
            throw new DraftDiscardError('Das Projekt wurde zwischenzeitlich von anderer Stelle gespeichert. Der Entwurf ist unveraendert — bitte das Projekt neu laden und erneut pruefen.', 'entwurf', e);
        }
        await kompensiere(`Bericht #${plan.previousNr} liess sich nicht vollstaendig speichern`, e);
    }

    // 3. Nicht-leere Behalte-Listen hat der Save eben transaktional geprunt.
    // Der Anker ist jetzt der Stand NACH dem eigenen Save (saveProjectToCloud
    // hat ihn nachgezogen); genau gegen den prueft die RPC.
    const leereTabellen = leereTabellenVon([['inspection_items', plan.restored.items], ['project_checklists', plan.restored.checklists]]);
    let ergebnis: 'ok' | 'version' | 'fehlt' = 'ok';
    if (leereTabellen.length > 0) {
        try {
            ergebnis = await raeumeTabellen(projectId, leereTabellen, knownProjectVersion(projectId) || anker);
            if (ergebnis === 'fehlt') throw new Error(RPC_FEHLT_TEXT);
        } catch (e) {
            await kompensiere(`Bericht #${plan.previousNr} ist gespeichert, aber Zeilen des Entwurfs stehen noch in der Datenbank`, e);
        }
    }
    if (ergebnis === 'version') {
        // Zwischen Save und Raeumen hat jemand anderes geschrieben. Keine
        // Kompensation: die ueberschriebe dessen Stand. Der Bericht #N-1 ist
        // gespeichert (und womoeglich schon fremd weitergeschrieben), die
        // Zeilen des Entwurfs stehen noch — das muss ein Mensch ansehen.
        throw new DraftDiscardError(
            `Der Stand ist UNEINHEITLICH: Bericht #${plan.previousNr} ist gespeichert, doch das Projekt wurde unmittelbar danach von anderer Stelle gespeichert, und die Zeilen des Entwurfs stehen noch in der Datenbank. Bitte das Projekt neu laden und den Betreuer informieren.`,
            'uneinheitlich',
        );
    }

    // Der Merker „Teilnehmer beim Oeffnen abfragen" gehoert zum verworfenen
    // Entwurf; er darf beim naechsten Anlegen nicht doppelt gelten.
    consumeParticipantPrompt(participantPromptKey(projectId, plan.draftNr));
};
